import React, { useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Search, MoreVertical } from "lucide-react";
import Dropdown from "./ui/Dropdown";
import CustomSelect from "./ui/CustomSelect";
import Pagination from "./ui/Pagination";
import { useAllKyc } from "../services/queries/queries";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";

function KYCTable() {
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { data, isError, isLoading, error } = useAllKyc();
  const navigate = useNavigate();
 

  const columns = [
    {
      accessorFn: (row) => `${row.user.firstName} ${row.user.lastName}`,
      header: "Full Name",
    },
    {
      accessorFn: (row) => row.user.email,
      header: "Email",
    },
    {
      accessorFn: (row) => row.user.country,
      header: "Country",
      cell: (info) =>
        info.getValue()?.charAt(0).toUpperCase() + info.getValue()?.slice(1),
    },
    {
      accessorFn: (row) => (row.status === "pending" ? "pending" : row.status === "approved" ? "approved" : "declined"),
      header: "Status",
      cell: (info) => {
        const status = info.getValue();
        const statusStyles = {
          pending: "bg-yellow-100 text-yellow-800",
          approved: "bg-green-100 text-green-800",
          declined: "bg-red-100 text-red-800",
        };

        return (
          <span
            className={`px-3 py-3 rounded-xl text-sm font-medium ${statusStyles[status]}`}
          >
            {status?.charAt(0).toUpperCase() + status?.slice(1)}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <Dropdown
          trigger={<MoreVertical className="h-4 w-4" />}
          items={[
            {
              label: "View Details",
              onClick: () => navigate(`/kyc-requests/${row.original.id}`),
            },
            { label: "Edit User", onClick: () => {} },
            {
              label: "Deactivate",
              onClick: () => {},
              className: "text-red-600",
            },
          ]}
        />
      ),
    },
  ];

  const filteredData = React.useMemo(() => {
    if (!data?.data) return [];

    return data.data?.filter((row) => {
      // Apply status filter
      const currentStatus = row.status === "pending" ? "pending" : row.status === "approved" ? "approved" : "declined";
      const statusMatches =
        statusFilter === "all" || currentStatus === statusFilter;

      // Apply search filter
      const searchTerms = [
        `${row.user.firstName} ${row.user.lastName}`,
        row.user.email,
        row.user.country,
        currentStatus,
      ];

      const searchMatches =
        !globalFilter ||
        searchTerms.some((term) =>
          String(term).toLowerCase().includes(globalFilter.toLowerCase())
        );

      return statusMatches && searchMatches;
    });
  }, [data, statusFilter, globalFilter]);

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 5,
      },
    },
  });

  const statusOptions = [
    { value: "all", label: "All KYC Requests" },
    { value: "pending", label: "Pending KYC Requests" },
    { value: "approved", label: "Approved KYC Requests" },
    { value: "declined", label: "Declined KYC Requests" },
  ];

  const handleSearch = (e) => {
    const value = e.target.value;
    setGlobalFilter(value);
    table.setPageIndex(0);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    table.setPageIndex(0);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen">
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-600">Loading user data...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-screen">
        <div className="flex-1 flex items-center justify-center">
          <p className="text-red-600">Error: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">KYC Requests</h1>
      </div>

      <div className="flex flex-col md:flex-row justify-between mb-6">
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Search"
            className="w-full pl-10 pr-4 py-3 border rounded-3xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={globalFilter}
            onChange={handleSearch}
          />
        </div>

        <CustomSelect
          value={statusFilter}
          onChange={handleStatusChange}
          options={statusOptions}
          placeholder="All KYC Requests"
          className="w-full md:w-64 z-50 mt-4 md:mt-0"
        />
      </div>

      <div className="overflow-hidden rounded border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-4 py-2 md:px-6 md:py-4 text-left text-sm font-medium text-gray-500 bg-[#F2F2F7]"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="border-b hover:bg-gray-50">
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-4 py-3 md:px-6 md:py-7 text-sm text-[#333333] font-sans"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {table.getRowModel().rows.length > 0 && (
        <div className="p-6 border-t border-gray-200 bg-gray-50/50 mt-6 md:mt-0">
          <Pagination
            currentPage={table.getState().pagination.pageIndex + 1}
            totalPages={table.getPageCount()}
            onPageChange={(page) => table.setPageIndex(page - 1)}
          />
        </div>
      )}
    </div>
  );
}

export default KYCTable;
